/**
 * Typed client for the Elyxeer API, admin surface.
 *
 * This is the only module in the app that calls `fetch`. Everything else goes through
 * the exported functions, so headers, error shape and the 401 handling live in exactly
 * one place.
 */

import { authHeaders } from "~/lib/auth-session";
import { notifyUnauthorized } from "~/lib/auth-token";
import { SLUG_PATTERN } from "~/lib/slug";
import type {
  AnalyticsContentPage,
  AnalyticsEvents,
  AnalyticsOverview,
  AnalyticsPromotions,
  AnalyticsRetention,
  AnalyticsTaxonomy,
  AnalyticsTimeseries,
} from "~/types/analytics-admin";
import type {
  AuthConfig,
  AuthUser,
  OTPRequestResponse,
  SessionResponse,
} from "~/types/auth";
import type {
  ChatMessage,
  ChatReadResponse,
  ChatThreadDetail,
  ChatThreadPage,
} from "~/types/chat-admin";
import type {
  CoverContext,
  CoverImageAdminPage,
  CoverPublished,
} from "~/types/covers-admin";
import type {
  DashboardHighlightAdmin,
  DashboardHighlightAdminCreate,
  DashboardHighlightAdminPage,
  DashboardHighlightAdminSort,
  DashboardHighlightAdminUpdate,
} from "~/types/dashboard-highlights-admin";
import type {
  EventAdmin,
  EventAdminCreate,
  EventAdminPage,
  EventAdminSort,
  EventAdminUpdate,
  EventQuestionAdmin,
  EventQuestionAdminPage,
  EventQuestionAdminUpdate,
  EventRegistrationAdminPage,
} from "~/types/events-admin";
import type {
  ContentFormat,
  NewsArticleAdminDetail,
  NewsArticleAdminPage,
  NewsArticleCreate,
  NewsArticleSort,
  NewsArticleUpdate,
  SortOrder,
} from "~/types/news-admin";
import type {
  AdminUser,
  AdminUserPage,
  AdminUserUpdate,
  MembershipInviteCreateResponse,
  MembershipInviteSummary,
} from "~/types/users-admin";

/** Absolute API origin — the panel is served from its own subdomain. */
export const API_BASE =
  import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8020";

const API_PREFIX = "/api/v1";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | undefined>;
  /** Attach the session token. Off for the sign-in endpoints, which have none yet. */
  authenticated?: boolean;
  signal?: AbortSignal;
}

async function readError(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { detail?: unknown };
    if (typeof body.detail === "string") return body.detail;
    // FastAPI's 422 returns a list of validation errors; surface the first message.
    if (Array.isArray(body.detail) && body.detail.length > 0) {
      const first = body.detail[0] as { msg?: string };
      if (first.msg) return first.msg;
    }
    return `Error ${response.status}`;
  } catch {
    return `Error ${response.status}`;
  }
}

async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = "GET", body, query, authenticated = true, signal } = options;

  const url = new URL(`${API_PREFIX}${path}`, API_BASE);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined) url.searchParams.set(key, String(value));
  }

  // FormData must pass through untouched, with no Content-Type header of our own —
  // the browser writes `multipart/form-data` with its boundary parameter itself.
  const isForm = body instanceof FormData;
  const headers: Record<string, string> = {};
  if (body !== undefined && !isForm)
    headers["Content-Type"] = "application/json";
  if (authenticated) Object.assign(headers, await authHeaders());

  const response = await fetch(url.toString(), {
    method,
    headers,
    body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
    signal,
  });

  // A 401 on an authenticated call means the session is gone. A 401 from the sign-in
  // endpoints means "wrong code" and belongs to the form, not to the session.
  if (response.status === 401 && authenticated) notifyUnauthorized();
  if (!response.ok)
    throw new ApiError(await readError(response), response.status);

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** The same request, for a file the browser will save rather than a body to parse. */
async function requestBlob(path: string, signal?: AbortSignal): Promise<Blob> {
  const url = new URL(`${API_PREFIX}${path}`, API_BASE);
  const response = await fetch(url.toString(), {
    headers: await authHeaders(),
    signal,
  });
  if (response.status === 401) notifyUnauthorized();
  if (!response.ok)
    throw new ApiError(await readError(response), response.status);
  return response.blob();
}

// ── Auth ──────────────────────────────────────────────────────────

export function fetchAuthConfig(): Promise<AuthConfig> {
  return request<AuthConfig>("/auth/config", { authenticated: false });
}

export function requestOtp(
  email: string,
  options: { captchaToken?: string; locale?: string } = {},
): Promise<OTPRequestResponse> {
  return request<OTPRequestResponse>("/auth/otp/request", {
    method: "POST",
    authenticated: false,
    body: {
      email,
      captcha_token: options.captchaToken ?? null,
      locale: options.locale ?? "cs",
    },
  });
}

export function verifyOtp(
  email: string,
  code: string,
): Promise<SessionResponse> {
  return request<SessionResponse>("/auth/otp/verify", {
    method: "POST",
    authenticated: false,
    body: { email, code },
  });
}

export function fetchMe(): Promise<AuthUser> {
  return request<AuthUser>("/auth/me");
}

// ── News administration ───────────────────────────────────────────

export function fetchAdminArticles(params: {
  locale?: string;
  search?: string;
  /** One format only — the recording picker on an event asks for webinar recordings. */
  format?: ContentFormat;
  sortBy?: NewsArticleSort;
  sortOrder?: SortOrder;
  limit?: number;
  offset?: number;
  signal?: AbortSignal;
}): Promise<NewsArticleAdminPage> {
  const { locale, search, format, sortBy, sortOrder, limit, offset, signal } =
    params;
  return request<NewsArticleAdminPage>("/admin/news", {
    query: {
      locale,
      search,
      format,
      sort_by: sortBy,
      sort_order: sortOrder,
      limit,
      offset,
    },
    signal,
  });
}

export function fetchAdminArticle(
  id: string,
  signal?: AbortSignal,
): Promise<NewsArticleAdminDetail> {
  return request<NewsArticleAdminDetail>(
    `/admin/news/${encodeURIComponent(id)}`,
    { signal },
  );
}

export function createArticle(
  article: NewsArticleCreate,
): Promise<NewsArticleAdminDetail> {
  return request<NewsArticleAdminDetail>("/admin/news", {
    method: "POST",
    body: article,
  });
}

export function updateArticle(
  id: string,
  update: NewsArticleUpdate,
): Promise<NewsArticleAdminDetail> {
  return request<NewsArticleAdminDetail>(
    `/admin/news/${encodeURIComponent(id)}`,
    { method: "PATCH", body: update },
  );
}

export function deleteArticle(id: string): Promise<void> {
  return request<void>(`/admin/news/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

// ── Cover library ───────────────────────────────────────────────

export function fetchCoverLibrary(
  params: {
    topic?: string;
    search?: string;
    limit: number;
    offset: number;
  },
  signal?: AbortSignal,
): Promise<CoverImageAdminPage> {
  return request<CoverImageAdminPage>("/admin/covers", {
    query: {
      topic: params.topic,
      search: params.search || undefined,
      limit: params.limit,
      offset: params.offset,
    },
    signal,
  });
}

/**
 * Upload an image into the cover library. The API normalises it (16:9 crop, JPEG,
 * metadata stripped), stamps the watermark unless told not to and publishes it. Nothing
 * shows it yet: the caller puts the URL in a form field and the save commits it, which
 * is why this works on a draft that has never been saved.
 */
export function uploadCover({
  file,
  watermark,
  context,
}: {
  file: File;
  watermark: boolean;
  context: CoverContext;
}): Promise<CoverPublished> {
  const form = new FormData();
  form.append("file", file);
  form.append("watermark", String(watermark));
  const slug = coverSlug(context);
  if (slug) form.append("slug", slug);
  const label = context.title.trim();
  if (label) form.append("label", label);
  for (const topic of context.topics) form.append("topics", topic);
  return request<CoverPublished>("/admin/covers", {
    method: "POST",
    body: form,
  });
}

/**
 * Have Gemini photograph a new cover from the draft's title and excerpt — or from the
 * editor's own description of the scene, which outranks them. Paid; slow. Published
 * into the library like an upload, and likewise shown nowhere until a save.
 */
export function generateCover({
  subject,
  context,
}: {
  subject: string | null;
  context: CoverContext;
}): Promise<CoverPublished> {
  return request<CoverPublished>("/admin/covers/generate", {
    method: "POST",
    body: {
      slug: coverSlug(context),
      title: context.title.trim(),
      excerpt: context.excerpt.trim(),
      topics: context.topics,
      // An article saves at most six; a draft may briefly hold more.
      keywords: context.keywords.slice(0, 6),
      subject,
    },
  });
}

/** A draft's slug may still be half-typed; one the API would refuse is left out. */
function coverSlug(context: CoverContext): string | null {
  return SLUG_PATTERN.test(context.slug) ? context.slug : null;
}

/** Take an unused cover out of the library (409 while anything still shows it). */
export function deleteCover(id: string): Promise<void> {
  return request<void>(`/admin/covers/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

// ── Calendar event administration ───────────────────────────────

export function fetchAdminEvents(params: {
  locale?: string;
  search?: string;
  isPublished?: boolean;
  isFeatured?: boolean;
  sortBy?: EventAdminSort;
  sortOrder?: SortOrder;
  limit?: number;
  offset?: number;
  signal?: AbortSignal;
}): Promise<EventAdminPage> {
  const {
    locale,
    search,
    isPublished,
    isFeatured,
    sortBy,
    sortOrder,
    limit,
    offset,
    signal,
  } = params;
  return request<EventAdminPage>("/admin/events", {
    query: {
      locale,
      search,
      is_published: isPublished === undefined ? undefined : String(isPublished),
      is_featured: isFeatured === undefined ? undefined : String(isFeatured),
      sort_by: sortBy,
      sort_order: sortOrder,
      limit,
      offset,
    },
    signal,
  });
}

export function fetchAdminEvent(
  id: string,
  signal?: AbortSignal,
): Promise<EventAdmin> {
  return request<EventAdmin>(`/admin/events/${encodeURIComponent(id)}`, {
    signal,
  });
}

export function createEvent(event: EventAdminCreate): Promise<EventAdmin> {
  return request<EventAdmin>("/admin/events", {
    method: "POST",
    body: event,
  });
}

export function updateEvent(
  id: string,
  update: EventAdminUpdate,
): Promise<EventAdmin> {
  return request<EventAdmin>(`/admin/events/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: update,
  });
}

export function deleteEvent(id: string): Promise<void> {
  return request<void>(`/admin/events/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

/** Who booked a place at this webinar, newest first. */
export function fetchAdminEventRegistrations(
  id: string,
  options: { limit?: number; offset?: number; signal?: AbortSignal } = {},
): Promise<EventRegistrationAdminPage> {
  return request<EventRegistrationAdminPage>(
    `/admin/events/${encodeURIComponent(id)}/registrations`,
    {
      query: { limit: options.limit, offset: options.offset },
      signal: options.signal,
    },
  );
}

/** The registrant list as a CSV file, for the marketing team. */
export function exportAdminEventRegistrations(id: string): Promise<Blob> {
  return requestBlob(
    `/admin/events/${encodeURIComponent(id)}/registrations/export`,
  );
}

export function deleteAdminEventRegistration(
  id: string,
  registrationId: string,
): Promise<void> {
  return request<void>(
    `/admin/events/${encodeURIComponent(id)}/registrations/${encodeURIComponent(registrationId)}`,
    { method: "DELETE" },
  );
}

/** The moderator's question list for this webinar, newest first. */
export function fetchAdminEventQuestions(
  id: string,
  options: {
    unansweredOnly?: boolean;
    includeHidden?: boolean;
    signal?: AbortSignal;
  } = {},
): Promise<EventQuestionAdminPage> {
  return request<EventQuestionAdminPage>(
    `/admin/events/${encodeURIComponent(id)}/questions`,
    {
      query: {
        unanswered_only: options.unansweredOnly ? "true" : undefined,
        include_hidden: options.includeHidden ? "true" : undefined,
      },
      signal: options.signal,
    },
  );
}

export function updateAdminEventQuestion(
  id: string,
  questionId: string,
  update: EventQuestionAdminUpdate,
): Promise<EventQuestionAdmin> {
  return request<EventQuestionAdmin>(
    `/admin/events/${encodeURIComponent(id)}/questions/${encodeURIComponent(questionId)}`,
    { method: "PATCH", body: update },
  );
}

// ── Dashboard Highlight administration ─────────────────────────

export function fetchAdminDashboardHighlights(params: {
  locale?: string;
  search?: string;
  isVisible?: boolean;
  sortBy?: DashboardHighlightAdminSort;
  sortOrder?: SortOrder;
  limit?: number;
  offset?: number;
  signal?: AbortSignal;
}): Promise<DashboardHighlightAdminPage> {
  const {
    locale,
    search,
    isVisible,
    sortBy,
    sortOrder,
    limit,
    offset,
    signal,
  } = params;
  return request<DashboardHighlightAdminPage>("/admin/dashboard-highlights", {
    query: {
      locale,
      search,
      is_visible: isVisible === undefined ? undefined : String(isVisible),
      sort_by: sortBy,
      sort_order: sortOrder,
      limit,
      offset,
    },
    signal,
  });
}

export function fetchAdminDashboardHighlight(
  id: string,
  signal?: AbortSignal,
): Promise<DashboardHighlightAdmin> {
  return request<DashboardHighlightAdmin>(
    `/admin/dashboard-highlights/${encodeURIComponent(id)}`,
    { signal },
  );
}

export function createDashboardHighlight(
  highlight: DashboardHighlightAdminCreate,
): Promise<DashboardHighlightAdmin> {
  return request<DashboardHighlightAdmin>("/admin/dashboard-highlights", {
    method: "POST",
    body: highlight,
  });
}

export function updateDashboardHighlight(
  id: string,
  update: DashboardHighlightAdminUpdate,
): Promise<DashboardHighlightAdmin> {
  return request<DashboardHighlightAdmin>(
    `/admin/dashboard-highlights/${encodeURIComponent(id)}`,
    { method: "PATCH", body: update },
  );
}

export function deleteDashboardHighlight(id: string): Promise<void> {
  return request<void>(
    `/admin/dashboard-highlights/${encodeURIComponent(id)}`,
    { method: "DELETE" },
  );
}

// ── User administration ──────────────────────────────────────────

export function fetchAdminUsers(params: {
  search?: string;
  isActive?: boolean;
  hasMembershipOverride?: boolean;
  limit?: number;
  offset?: number;
  signal?: AbortSignal;
}): Promise<AdminUserPage> {
  const { search, isActive, hasMembershipOverride, limit, offset, signal } =
    params;
  return request<AdminUserPage>("/admin/users", {
    query: {
      search,
      is_active: isActive === undefined ? undefined : String(isActive),
      has_membership_override:
        hasMembershipOverride === undefined
          ? undefined
          : String(hasMembershipOverride),
      limit,
      offset,
    },
    signal,
  });
}

export function fetchAdminUser(
  id: string,
  signal?: AbortSignal,
): Promise<AdminUser> {
  return request<AdminUser>(`/admin/users/${encodeURIComponent(id)}`, {
    signal,
  });
}

export function updateAdminUser(
  id: string,
  update: AdminUserUpdate,
): Promise<AdminUser> {
  return request<AdminUser>(`/admin/users/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: update,
  });
}

export function deleteAdminUser(id: string): Promise<void> {
  return request<void>(`/admin/users/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

// ── Override-membership invitations ──────────────────────────────

export function fetchMembershipInvites(
  signal?: AbortSignal,
): Promise<MembershipInviteSummary[]> {
  return request<MembershipInviteSummary[]>("/admin/membership-invites", {
    signal,
  });
}

export function createMembershipInvite(
  email: string,
): Promise<MembershipInviteCreateResponse> {
  return request<MembershipInviteCreateResponse>("/admin/membership-invites", {
    method: "POST",
    body: { email },
  });
}

export function revokeMembershipInvite(id: string): Promise<void> {
  return request<void>(`/admin/membership-invites/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

// ── Member conversations ─────────────────────────────────────────

export function fetchAdminChatThreads(params: {
  search?: string;
  unreadOnly?: boolean;
  limit?: number;
  offset?: number;
  signal?: AbortSignal;
}): Promise<ChatThreadPage> {
  const { search, unreadOnly, limit, offset, signal } = params;
  return request<ChatThreadPage>("/admin/chat/threads", {
    query: {
      search,
      unread_only: unreadOnly === undefined ? undefined : String(unreadOnly),
      limit,
      offset,
    },
    signal,
  });
}

export function fetchAdminChatThread(
  id: string,
  options: { limit?: number; offset?: number; signal?: AbortSignal } = {},
): Promise<ChatThreadDetail> {
  return request<ChatThreadDetail>(
    `/admin/chat/threads/${encodeURIComponent(id)}`,
    {
      query: { limit: options.limit, offset: options.offset },
      signal: options.signal,
    },
  );
}

export function sendAdminChatMessage(
  id: string,
  body: string,
): Promise<ChatMessage> {
  return request<ChatMessage>(
    `/admin/chat/threads/${encodeURIComponent(id)}/messages`,
    { method: "POST", body: { body } },
  );
}

export function markAdminChatThreadRead(id: string): Promise<ChatReadResponse> {
  return request<ChatReadResponse>(
    `/admin/chat/threads/${encodeURIComponent(id)}/read`,
    { method: "POST" },
  );
}

// ── Analytics ────────────────────────────────────────────────────────────────

/** The window every report shares, as inclusive Europe/Prague calendar days. */
export interface AnalyticsRangeParams {
  from: string;
  to: string;
  locale?: string;
  signal?: AbortSignal;
}

function rangeQuery(params: AnalyticsRangeParams) {
  return { date_from: params.from, date_to: params.to, locale: params.locale };
}

export function fetchAnalyticsOverview(
  params: AnalyticsRangeParams,
): Promise<AnalyticsOverview> {
  return request<AnalyticsOverview>("/admin/analytics/overview", {
    query: rangeQuery(params),
    signal: params.signal,
  });
}

export function fetchAnalyticsTimeseries(
  params: AnalyticsRangeParams,
): Promise<AnalyticsTimeseries> {
  return request<AnalyticsTimeseries>("/admin/analytics/timeseries", {
    query: rangeQuery(params),
    signal: params.signal,
  });
}

export function fetchAnalyticsContent(
  params: AnalyticsRangeParams & {
    subjectType?: string;
    format?: string;
    topic?: string;
    limit?: number;
    offset?: number;
  },
): Promise<AnalyticsContentPage> {
  return request<AnalyticsContentPage>("/admin/analytics/content", {
    query: {
      ...rangeQuery(params),
      subject_type: params.subjectType,
      format: params.format,
      topic: params.topic,
      limit: params.limit,
      offset: params.offset,
    },
    signal: params.signal,
  });
}

export function fetchAnalyticsTaxonomy(
  params: AnalyticsRangeParams,
): Promise<AnalyticsTaxonomy> {
  return request<AnalyticsTaxonomy>("/admin/analytics/taxonomy", {
    query: rangeQuery(params),
    signal: params.signal,
  });
}

export function fetchAnalyticsEvents(
  params: AnalyticsRangeParams,
): Promise<AnalyticsEvents> {
  return request<AnalyticsEvents>("/admin/analytics/events", {
    query: rangeQuery(params),
    signal: params.signal,
  });
}

export function fetchAnalyticsPromotions(
  params: AnalyticsRangeParams & { slot?: "highlight" | "feed" },
): Promise<AnalyticsPromotions> {
  return request<AnalyticsPromotions>("/admin/analytics/highlights", {
    query: { ...rangeQuery(params), slot: params.slot },
    signal: params.signal,
  });
}

export function fetchAnalyticsRetention(
  params: AnalyticsRangeParams,
): Promise<AnalyticsRetention> {
  return request<AnalyticsRetention>("/admin/analytics/retention", {
    query: { date_from: params.from, date_to: params.to },
    signal: params.signal,
  });
}
